/**
 * Task detail view — white Task Edit sheet (native proportions) on the
 * execute PWA. Photo stays full-bleed; form sheet is paper/white.
 *
 * Layout: 3:4 PhotoMapViewer → sheet (From + status → title → description
 * → ActionCards) → FloatingFooter (Back + one primary CTA).
 * Activity timeline is omitted on web (native-only).
 */
import { useState, useCallback } from 'react';
import { PRIMARY_ACTION as PIPELINE_PRIMARY_ACTION } from '@x9/task-pipeline';
import type { ResolveTaskResponse, TaskStatus } from '../types';
import { finalizeWebAssigneeAfterVerify, updateTaskStatusFromWeb } from '../services/api';
import { hasVerifiedPhoneOnSession } from '@/services/auth';
import { HOME_PATH, useHasHome } from '@/components/useHasHome';
import { getFirebaseAuth } from '@/config/firebase';
import { goBackInApp, inAppBackTarget, navigateTo } from '@/navigation';
import { homeOutline } from 'ionicons/icons';
import StatusBadge from '../components/StatusBadge';
import PhotoMapViewer from '../components/PhotoMapViewer';
import { useTaskFollowers } from '../../home/useTaskFollowers';
import ActionCard from '../components/ActionCard';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from '../components/FloatingFooter';
import DenyStep from './DenyStep';
import CompleteStep from './CompleteStep';
import ConvertStep from './ConvertStep';

interface Props {
  shortId: string;
  task: ResolveTaskResponse['task'];
  onTaskUpdated: () => void;
  onAssigneeVerificationRequired?: () => void;
  onClose?: () => void;
}

type Modal = 'none' | 'deny' | 'complete' | 'convert';

interface PrimaryAction {
  label: string;
  next: TaskStatus;
}

const ACTION_LABEL: Record<'accept' | 'start' | 'complete', string> = {
  accept: 'Accept',
  start: 'Start working',
  complete: 'Mark complete',
};

const PRIMARY_ACTION: Partial<Record<TaskStatus, PrimaryAction>> = Object.fromEntries(
  Object.entries(PIPELINE_PRIMARY_ACTION).map(([status, action]) => [
    status,
    { label: ACTION_LABEL[action.actionKey], next: action.nextStatus },
  ]),
) as Partial<Record<TaskStatus, PrimaryAction>>;

function formatDueDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export default function TaskViewStep({
  shortId,
  task,
  onTaskUpdated,
  onAssigneeVerificationRequired,
  onClose,
}: Props) {
  const [modal, setModal] = useState<Modal>('none');
  const [updating, setUpdating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const hasHome = useHasHome();

  const handlePrimary = useCallback(
    async (newStatus: TaskStatus) => {
      if (newStatus === 'completed') {
        setModal('complete');
        return;
      }
      setUpdating(true);
      setActionError(null);
      try {
        const res = await updateTaskStatusFromWeb({ shortId, newStatus });
        if (!res.ok) {
          if (res.code === 'assignee_verify_required' || res.code === 'phone_required') {
            if (await hasVerifiedPhoneOnSession()) {
              try {
                await finalizeWebAssigneeAfterVerify(shortId);
                const retry = await updateTaskStatusFromWeb({ shortId, newStatus });
                if (retry.ok) {
                  onTaskUpdated();
                  return;
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to finalize assignee before status update:', err);
              }
            }
            if (res.code === 'assignee_verify_required') {
              onAssigneeVerificationRequired?.();
              return;
            }
          }
          if (res.code === 'already_claimed' || res.code === 'not_available') {
            onTaskUpdated();
            return;
          }
          setActionError(res.error || 'Could not update this task.');
          return;
        }
        if (res.assigneeVerificationRequired) {
          if (await hasVerifiedPhoneOnSession()) {
            try {
              await finalizeWebAssigneeAfterVerify(shortId);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Failed to finalize assignee after accept:', err);
            }
            onTaskUpdated();
            return;
          }
          onAssigneeVerificationRequired?.();
          return;
        }
        onTaskUpdated();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Status update failed:', err);
        setActionError('Could not update this task. Check your connection and try again.');
      } finally {
        setUpdating(false);
      }
    },
    [shortId, onTaskUpdated, onAssigneeVerificationRequired],
  );

  if (modal === 'deny') {
    return (
      <DenyStep
        shortId={shortId}
        onDone={() => {
          setModal('none');
          onTaskUpdated();
        }}
        onCancel={() => setModal('none')}
      />
    );
  }
  if (modal === 'complete') {
    return (
      <CompleteStep
        shortId={shortId}
        onDone={() => {
          setModal('none');
          onTaskUpdated();
        }}
        onCancel={() => setModal('none')}
      />
    );
  }
  if (modal === 'convert') {
    return <ConvertStep onBack={() => setModal('none')} />;
  }

  const displayStatus = task.displayStatus ?? task.status;
  const isOwnerViewer = task.viewerRole === 'owner';
  const readerUid = getFirebaseAuth()?.currentUser?.uid ?? null;
  const { followers } = useTaskFollowers(task.id, {
    readerUid,
    taskOwnerUid: isOwnerViewer ? readerUid : null,
    taskAssigneeUid: isOwnerViewer ? null : readerUid,
  });
  const overlayAssignee = task.executorDisplay
    ? { name: task.executorDisplay, photoURL: null }
    : null;
  const isClaimedByMe = task.executionAvailability === 'claimed_by_me';
  const isPending = task.status === 'pending';
  const isReopenedAssignee = displayStatus === 'reopened' || (isClaimedByMe && isPending);
  const primary =
    isOwnerViewer ||
    task.executionAvailability === 'claimed_by_other' ||
    task.executionAvailability === 'terminal'
      ? undefined
      : isReopenedAssignee
        ? PRIMARY_ACTION.accepted
        : PRIMARY_ACTION[task.status];
  const isActionable =
    !isOwnerViewer &&
    isClaimedByMe &&
    (isPending || task.status === 'accepted' || task.status === 'inProgress');
  const isClaimedByOther = !isOwnerViewer && task.executionAvailability === 'claimed_by_other';
  const isTerminal = !isOwnerViewer && task.executionAvailability === 'terminal';

  /*
   * One exit, never both. Back walks the in-app stack (run → list → home).
   * Home is only for a cold / shared link that has nowhere inside X9 to
   * return to — otherwise it would skip the run the reader just came from.
   */
  const canGoBack = !!onClose || !!inAppBackTarget();
  const showBack = canGoBack || !hasHome;
  const showHome = hasHome && !canGoBack;
  const goHome = () => navigateTo(HOME_PATH);
  const goBack = () => {
    if (onClose) return onClose();
    if (goBackInApp()) return;
    if (hasHome) return navigateTo(HOME_PATH);
    return window.history.back();
  };

  if (isClaimedByOther || isTerminal) {
    return (
      <div className="page-shell">
        <div className="page-body flex flex-col justify-center text-center">
          <StatusBadge
            status={displayStatus}
            surface="paper"
            variant="filled"
            className="justify-center"
          />
          <h1 className="mt-4 text-[28px] font-bold tracking-tight text-x9-ink">
            {isClaimedByOther ? 'Task already accepted' : 'Task closed'}
          </h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-x9-marineSoft">
            {isClaimedByOther
              ? `${task.executorDisplay || 'Another executor'} is already working on this task.`
              : 'This task is no longer open for execution.'}
          </p>
        </div>
        <FloatingFooter>
          {showHome ? (
            <FloatingFooterNavButton label="Home" icon={homeOutline} onClick={goHome} />
          ) : null}
          {showBack ? <FloatingFooterNavButton label="Back" onClick={goBack} /> : null}
          <FloatingFooterCta
            tone="muted"
            onClick={() => {
              window.location.href = 'https://x9.app';
            }}
          >
            Learn about X9
          </FloatingFooterCta>
        </FloatingFooter>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-body !px-0">
        <PhotoMapViewer
          photoUrl={task.photoUrl}
          hasOverlay={task.hasOverlay}
          highlightSvg={task.highlightSvg}
          drawingPaths={task.drawingPaths}
          location={task.location}
          title={task.title}
          status={displayStatus}
          assignee={overlayAssignee}
          followers={followers}
        />

        <div className="execute-sheet">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-xs font-medium text-x9-marineSoft">
              {isOwnerViewer
                ? task.executorDisplay
                  ? `Assigned to ${task.executorDisplay}`
                  : 'Assigned to someone else'
                : `From ${task.creatorName}`}
            </p>
            <StatusBadge
              status={displayStatus}
              surface="paper"
              variant="filled"
              size="sm"
              className="shrink-0"
            />
          </div>

          <h1 className="min-h-[50px] text-[28px] font-bold leading-tight tracking-tight text-x9-ink">
            {task.title}
          </h1>
          {task.description && (
            <p className="mt-2 min-h-[4rem] whitespace-pre-wrap text-sm leading-relaxed text-x9-marineSoft">
              {task.description}
            </p>
          )}

          {task.dueDate && (
            <p className="mt-3 text-xs text-x9-marineSoft/80">Due {formatDueDate(task.dueDate)}</p>
          )}

          {actionError && (
            <div className="mt-4 rounded-[15px] border border-x9-error/30 bg-x9-error/5 p-4 text-sm text-x9-error">
              {actionError}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3">
            {isActionable && (
              <ActionCard
                title="Can't do this anymore?"
                description="Tell the creator you need to step away. They'll review before the link opens again."
                ctaLabel="Reject task"
                variant="outline-danger"
                onClick={() => setModal('deny')}
                disabled={updating}
              />
            )}
            {!isOwnerViewer ? (
              <ActionCard
                title="Want your own workspace?"
                description="Reassign this to someone else, or create tasks for your contacts in the app."
                ctaLabel="Reassign or create your own task"
                onClick={() => setModal('convert')}
                disabled={updating}
              />
            ) : null}
          </div>
        </div>
      </div>

      <FloatingFooter>
        {showHome ? (
          <FloatingFooterNavButton
            label="Home"
            icon={homeOutline}
            onClick={goHome}
            disabled={updating}
          />
        ) : null}
        {showBack ? (
          <FloatingFooterNavButton label="Back" onClick={goBack} disabled={updating} />
        ) : null}
        {primary ? (
          <FloatingFooterCta
            onClick={() => handlePrimary(primary.next)}
            disabled={updating}
            aria-busy={updating}
          >
            {updating ? '…' : primary.label}
          </FloatingFooterCta>
        ) : (
          <FloatingFooterCta tone="muted" disabled>
            {displayStatus}
          </FloatingFooterCta>
        )}
      </FloatingFooter>
    </div>
  );
}
